import React, { useRef, useState } from "react";
import { ClickAwayListener, makeStyles, MenuItem, MenuList, Paper, Popper } from "@material-ui/core";
import jsPDF from "jspdf";
import { useSelector } from "react-redux";
import { t } from 'i18next';
import { toPng } from "html-to-image";
import { getMaxValueLength } from "helpers/lisaHelpers";
import { ChatProcessing } from "./ChatProcessing";
import menuIcon from "assets/images/icons/menu-icon.svg";
import menuOpenIcon from "assets/images/icons/menu-open-icon.svg";
import MaestroLogo from 'assets/images/maestro_logo_png_light.png';
import ChatLogo from 'assets/images/chatLogo2.png';
import ExportIcon from 'assets/images/icons/export-icon.svg';
import CloseIcon from 'assets/images/icons/close-icon.svg';

const useStyles = makeStyles(theme => ({
    hamburgerIcon: {
        cursor: 'pointer'
    },
    menuPopper: {
        zIndex: "999",
    },
    menuItem: {
        color: theme.palette.common.black,
        '& img': {
            marginRight: theme.spacing(1)
        },
        '&:hover': {
            backgroundColor: theme.palette.grey[100]
        }
    },
    processingContainer: {
        width: '100%',
        height: '100%',
        backgroundColor: '#ffffffdd',
        position: 'absolute',
        alignContent: 'center',
        left: '0',
        top: '50px',
        paddingBottom: '50px',
        zIndex: '9'
    }
}))

export const exportChat = async ({ chatData, setIsDownloading, includeCharts = false }) => {
    const headingColor1 = "#1915C9";
    const headingColor2 = "#00695C";
    const textColor = "#000";

    try {
        setIsDownloading(true);
        const doc = new jsPDF('portrait', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const font = doc.getFont();
        const fontSize = 11;
        const margin = 12;
        const lineHeight = 6;
        const usableWidth = pageWidth - margin * 2;
        let yPosition = 25;

        doc.setFontSize(fontSize)
        // doc.setLineHeightFactor(1.5)
        doc.setTextColor(textColor);
        // message with background
        const addMessageBox = (x, y, width, height, bgColor, text) => {
            doc.setFillColor(...bgColor);
            doc.roundedRect(x, y, width, height, 4, 4, "F");
            const lines = doc.splitTextToSize(text, width - 8);
            doc.text(lines, x + 4, y + 8);
        };

        const addHeader = () => {
            doc.addImage(MaestroLogo, "SVG", 1, 1, 70, 13);
            doc.addImage(ChatLogo, "SVG", pageWidth - 15, 1, 13, 13);
            doc.setFontSize(16);
            doc.text("LISA", pageWidth - 30, 10);
            doc.setFontSize(fontSize);
        }
        // adding header to pdf
        addHeader();

        const addPageIfNeeded = ({ contentHeight }) => {
            if ((yPosition + contentHeight + 5) > pageHeight) {
                doc.addPage();
                yPosition = 25;
                addHeader();
            }
        }

        if (chatData?.length) {
            for (const item of chatData) {
                // Question text
                const questionText = item?.metadata?.query_text || "";
                const questionWidth = questionText?.length > 77 ? 150 : questionText?.length * 2 + 8;
                const questionHeight = doc.splitTextToSize(questionText, questionWidth).length * lineHeight + 8;
                const xPosition = usableWidth - questionWidth + 20;
                addMessageBox(xPosition, yPosition, questionWidth, questionHeight, ['#dadeef'], questionText);
                yPosition = yPosition + questionHeight + lineHeight;

                // Answer Text
                const answer = doc.splitTextToSize(item?.message?.final_answer || "", 150);
                const contextualizedQuestion = doc.splitTextToSize(item?.metadata?.contextualized_question || "", 150);

                const calculations = item?.message?.Calculations || {};
                const calcLabelLength = getMaxValueLength(Object.keys(calculations));
                const calcLabelWidth = calcLabelLength * 2.3 + 8;
                const calcContentHeight = Object.values(calculations).reduce((total, val) => {
                    return total + doc.splitTextToSize(val, 150 - calcLabelWidth).length * lineHeight
                }, 0)

                const queryFilters = item?.message?.query_filters || {};
                const filterLabelLength = getMaxValueLength(Object.keys(queryFilters));
                const filterLabelWidth = filterLabelLength * 2.3 + 8;
                const filterContentHeight = Object.values(queryFilters).reduce((total, val) => {
                    return total + doc.splitTextToSize(val, 150 - filterLabelLength).length * lineHeight
                }, 0)

                let answerHeight = (answer.length || 0) * lineHeight + calcContentHeight + filterContentHeight +
                    (contextualizedQuestion?.length || 0) * lineHeight + (lineHeight * 5.5);

                if (calcContentHeight > 0) {
                    answerHeight = answerHeight + lineHeight * 1.5;
                }
                if (filterContentHeight > 0) {
                    answerHeight = answerHeight + lineHeight * 1.5;
                }
                if (contextualizedQuestion > 0) {
                    answerHeight = answerHeight + lineHeight * 1.5;
                }

                addPageIfNeeded({ contentHeight: answerHeight })
                addMessageBox(margin, yPosition, 160, answerHeight, ['#f2f2f2'], "");
                yPosition = yPosition + lineHeight;
                // bot name in the first line of answer
                doc.setTextColor(headingColor1);
                doc.setFont(font?.fontName, 'bold');
                doc.text("LISA:", margin + 4, yPosition);
                // answer text
                doc.setTextColor(textColor);
                doc.setFont(font?.fontName, 'normal');
                yPosition = yPosition + lineHeight;
                doc.text(answer, margin + 4, yPosition);
                yPosition = yPosition + answer?.length * lineHeight + lineHeight;

                // retrieval details heading
                doc.setTextColor(headingColor1);
                doc.setFont(font?.fontName, 'bold');
                doc.text(t('retrieval_details'), margin + 4, yPosition);
                yPosition = yPosition + lineHeight;

                // Question interpreted as 
                if (contextualizedQuestion?.length) {
                    doc.setTextColor(headingColor2);
                    doc.text(`${t('question_interpreted_as')}:`, margin + 4, yPosition);
                    doc.setTextColor(textColor);
                    doc.setFont(font?.fontName, 'normal');
                    yPosition = yPosition + lineHeight;
                    doc.text(contextualizedQuestion, margin + 4, yPosition);
                    yPosition = yPosition + (contextualizedQuestion.length || 0) * lineHeight + lineHeight;
                }

                // Calcualtions
                if (calcLabelLength) {
                    doc.setFont(font?.fontName, 'bold');
                    doc.setTextColor(headingColor2);
                    doc.text(`${t('calculations')}:`, margin + 4, yPosition);
                    doc.setTextColor(textColor);

                    yPosition = yPosition + lineHeight;
                    Object.keys(calculations).forEach(key => {
                        const calcValue = doc.splitTextToSize(calculations[key], 152 - calcLabelWidth);
                        doc.setFont(font?.fontName, 'bold');
                        const keyText = typeof key === "string" ? key.replace(/_/g, " ") : String(key ?? "");
                        doc.text(`${keyText}:`, margin + 4, yPosition);
                        doc.setFont(font?.fontName, 'normal');
                        doc.text(calcValue, margin + calcLabelWidth, yPosition);
                        yPosition = yPosition + calcValue.length * lineHeight;
                    })
                }

                // Filters Applied
                if (filterLabelLength) {
                    yPosition = yPosition + lineHeight;
                    doc.setFont(font?.fontName, 'bold');
                    doc.setTextColor(headingColor2);
                    doc.text(`${t('filters_applied')}:`, margin + 4, yPosition);
                    doc.setTextColor(textColor);
                    yPosition = yPosition + lineHeight;
                    Object.keys(queryFilters).forEach(key => {
                        const filterValue = doc.splitTextToSize(queryFilters[key], 152 - filterLabelWidth);
                        doc.setFont(font?.fontName, 'bold');
                        const keyText = typeof key === "string" ? key.replace(/_/g, " ") : String(key ?? "");
                        doc.text(`${keyText}:`, margin + 4, yPosition);
                        doc.setFont(font?.fontName, 'normal');
                        doc.text(filterValue, margin + filterLabelWidth, yPosition);
                        yPosition = yPosition + filterValue.length * lineHeight;
                    })
                }

                // table data
                const tableData = item.tableData
                if (tableData?.length) {
                    const dataKeys = Object.keys(tableData[0])
                    yPosition = yPosition + lineHeight;
                    doc.autoTable({
                        startY: yPosition,
                        margin: { top: 20, right: 40 },
                        head: [(dataKeys || []).map(v =>
                            typeof v === "string" ? v.replace(/_/g, " ") : String(v ?? "")
                        )],
                        body: tableData.map(v => Object.values(v)),
                        styles: { fontSize: fontSize },
                        headStyles: { fontSize: fontSize + 1 },
                        bodyStyles: { fontSize: fontSize },
                        showHead: "everyPage",
                        didDrawPage: () => { addHeader() }
                    });
                    yPosition = doc.lastAutoTable.finalY + lineHeight
                }

                // chart images
                const responseContainer = document.getElementById(`response-${item?.metadata?.question_uuid}`);
                const chartContainer = responseContainer.querySelector('.hiddenChartContainer')
                if (includeCharts && chartContainer?.children?.length > 0) {
                    for (const chart of Array.from(chartContainer.children)) {
                        // Create image
                        const imgData = await toPng(chart, {
                            cacheBust: true,
                            pixelRatio: 2,
                            backgroundColor: '#fff'
                        });

                        // Get image dimensions
                        const img = new Image();
                        await new Promise((resolve) => {
                            img.onload = resolve;
                            img.src = imgData;
                        });
                        const imgWidth = usableWidth - 30;
                        const imgHeight = (img.height / img.width) * imgWidth;

                        // Add new page if needed
                        if ((yPosition + imgHeight + lineHeight) > pageHeight) {
                            doc.addPage();
                            yPosition = 25;
                            addHeader();
                        }

                        // Add image to PDF
                        doc.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
                        yPosition += imgHeight + lineHeight;
                    }
                }
            }
        }
        const curDate = new Date().toISOString().split('T')[0];
        doc.save(`LISA_conversation_${curDate}.pdf`);
        setIsDownloading(false);
    } catch (err) {
        setIsDownloading(false);
        console.error(err)
    }
}

export const ChatHeaderMenu = ({ closeChat }) => {
    const { chatData } = useSelector(({ lisa }) => lisa)
    const [menuOpen, setMenuOpen] = useState(false);
    const classes = useStyles();
    const menuRef = useRef(null)
    const [isDownloading, setIsDownloading] = useState(false)

    const toggleMenu = () => {
        setMenuOpen(!menuOpen)
    }

    const handleClose = () => {
        setMenuOpen(false);
        closeChat && closeChat();
    }
    return <>
        <div className={classes.hamburgerIcon} onClick={toggleMenu} ref={menuRef} data-testid="chatHeaderMenu" role="button" >
            {menuOpen ?
                <img src={menuOpenIcon} alt="" />
                : <img src={menuIcon} alt="" />}
        </div>
        {isDownloading &&
            <div className={classes.processingContainer}>
                <ChatProcessing chatProcess={<b>{t('your_download_will_begin_shortly')}...</b>} />
            </div>
        }
        <Popper open={menuOpen} anchorEl={menuRef.current} transition placement="bottom-end" className={classes.menuPopper} >
            <Paper>
                <ClickAwayListener onClickAway={toggleMenu}>
                    <MenuList autoFocusItem={menuOpen}>
                        <MenuItem onClick={() => { exportChat({ chatData, setIsDownloading }) }} className={classes.menuItem} >
                            <img src={ExportIcon} alt="" />
                            {t('export_without_chart')}
                        </MenuItem>
                        <MenuItem onClick={() => { exportChat({ chatData, setIsDownloading, includeCharts: true }) }} className={classes.menuItem} >
                            <img src={ExportIcon} alt="" />
                            {t('export_with_chart')}
                        </MenuItem>
                        <MenuItem onClick={handleClose} className={classes.menuItem}>
                            <img src={CloseIcon} alt="" />
                            {t('close_window')}
                        </MenuItem>
                    </MenuList>
                </ClickAwayListener>
            </Paper>
        </Popper>
    </>
}